import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { leases as leaseApi } from '@/api/endpoints'
import type { Lease, LeasePowerAction } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Err } from '@/components/Err'
import { POWER, type PowerChip, isUnreachable } from '@/domain/lease'
import { ACTION, ACTION_DANGER, ACTION_PLAIN, ACTION_WAITING } from './chips'

// VM 상세 제목 줄의 조작 칩 한 줄: 지금 상태에 맞는 전원 칩들과 반납. 사용자의 내 VM 상세와 관리자의 VM 상세가
// 이 한 컴포넌트를 쓴다 — 두 화면이 같은 VM 에 다른 조작을 허락하지 않게. 누가 이 줄을 보는가는 부르는 쪽이 정한다(`mayChange`).
// 하나가 진행 중이면 줄 전체가 잠긴다 — 백엔드도 같은 기계에 겹치는 요청을 거절하고, 사람도 그 결과를 보고 다음을 정한다.
// 칩의 낱말은 도는 동안에도 그대로다 — `실행 중` 은 VM 의 상태 낱말이라 진행을 그렇게 말하면 이미 실행된 것으로 읽힌다.
// `lead` 는 전원 칩 앞, `extra` 는 반납 앞에 서는 그 화면의 칩이고, 도는 동안 잠기도록 진행 여부를 받는다.
export function PowerActions({
  lease: l,
  onChanged,
  endTo,
  lead,
  extra,
}: {
  lease: Lease
  onChanged: () => void
  /** 반납한 뒤 돌아갈 목록. */
  endTo: string
  lead?: (busy: boolean) => ReactNode
  extra?: (busy: boolean) => ReactNode
}) {
  const [confirm, setConfirm] = useState<PowerChip | null>(null)
  const [pending, setPending] = useState<LeasePowerAction | null>(null)
  const [error, setError] = useState<unknown>(null)
  const run = async (p: PowerChip) => {
    setPending(p.action)
    setError(null)
    try {
      await leaseApi.power(l.id, p.action)
      setConfirm(null)
      onChanged()
    } catch (err) {
      setError(err)
    } finally {
      setPending(null)
    }
  }
  const busy = pending !== null
  const shown = l.state === 'active' && l.vm ? POWER.filter((p) => p.when === l.vm?.state) : []
  // 허락된 변경은 아직 적용되지 않았다 — 이 기계를 쓰는 사람이 재부팅할 때 적용된다(B-29).
  const waiting = l.pending_change?.state === 'approved'
  // 노드가 응답하지 않는 동안(B-23) 전원 칩은 잠긴다 — 백엔드도 같은 요청을 거절한다.
  const locked = isUnreachable(l.vm)
  return (
    <div className="flex flex-col items-end gap-1.5">
      <div className="flex items-center gap-1.5 max-sm:flex-wrap max-sm:justify-end">
        {lead?.(busy)}
        {shown.map((p) => {
          // 승인된 변경은 이 재부팅에서 적용된다(B-29) — 칩이 스스로 그것을 말한다.
          const carries = waiting && (p.action === 'reboot' || p.action === 'start')
          const chip = (
            <button
              key={p.action}
              type="button"
              className={`${ACTION} ${carries ? ACTION_WAITING : ACTION_PLAIN}`}
              disabled={busy || locked}
              onClick={() => (p.confirm ? (setError(null), setConfirm(p)) : run(p))}
            >
              {p.word}
            </button>
          )
          if (!carries) return chip
          return (
            <Tooltip key={p.action}>
              <TooltipTrigger asChild>{chip}</TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                재부팅이 필요합니다.
                <br />
                자원 확장 요청이 승인되었습니다.
              </TooltipContent>
            </Tooltip>
          )
        })}
        {extra?.(busy)}
        <EndButton lease={l} onEnded={onChanged} endTo={endTo} disabled={busy} />
      </div>
      {error && !confirm ? <Err error={error} /> : null}
      <Dialog open={confirm !== null} onOpenChange={(v) => (v || pending ? undefined : setConfirm(null))}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {l.name} {confirm?.word}
            </DialogTitle>
          </DialogHeader>
          <Err error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirm(null)} disabled={busy}>
              취소
            </Button>
            <Button type="button" onClick={() => confirm && run(confirm)} disabled={busy}>
              {confirm ? (pending === confirm.action ? `${confirm.word} 중` : confirm.word) : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// 반납은 정지·보존이다. 대화 상자는 제목 하나와 버튼 둘이다.
function EndButton({ lease: l, onEnded, endTo, disabled = false }: { lease: Lease; onEnded: () => void; endTo: string; disabled?: boolean }) {
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const end = async () => {
    setBusy(true)
    setError(null)
    try {
      await leaseApi.end(l.id)
      setOpen(false)
      onEnded()
      nav(endTo)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        className={`${ACTION} ${ACTION_DANGER}`}
        onClick={() => setOpen(true)}
        disabled={disabled || l.state !== 'active'}
      >
        반납
      </button>
      <Dialog open={open} onOpenChange={(v) => (v ? undefined : setOpen(false))}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{l.name} 반납</DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-muted-foreground">VM 이 정지되고 보존됩니다.</p>
          <Err error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              취소
            </Button>
            <Button type="button" onClick={end} disabled={busy} className="bg-danger text-white hover:bg-danger/90">
              반납
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
